'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, AlertCircle, ChevronRight, ChevronLeft, CheckCircle2, Upload, Info } from 'lucide-react';
import { toast } from 'sonner';

interface HealerRegisterData {
  fullName: string;
  professionalName: string;
  email: string;
  mobile: string;
  bio: string;
  qualifications: string;
  certifications: string;
  experience: string;
  specializations: string[];
  languages: string[];
  pricePerMinute: number;
  minimumCharges: number;
  password: string;
  confirmPassword: string;
  agreeHealer: boolean;
  agreeAccurate: boolean;
  agreeConfidential: boolean;
  agreeConductProfessional: boolean;
  agreePaymentStructure: boolean;
}

const specializationOptions = [
  'Marriage & Partnership', 'Family Relationships', 'Parent-Child Relationships',
  'Emotional Healing', 'Breakup Recovery', 'Self-Love', 'Forgiveness',
  'Communication Issues', 'Trust Issues', 'Conflict Resolution',
  'Workplace Relationships', 'Friendship', 'Inner Child Work',
  'Grief & Emotional Support', 'Spiritual Relationship Healing',
];

const languageOptions = ['English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Bengali', 'Marathi', 'Gujarati'];

interface HealerRegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function HealerRegisterForm({ onSwitchToLogin }: HealerRegisterFormProps) {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState<string[]>([]);
  const [selectedLangs, setSelectedLangs] = useState<string[]>([]);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const photoInputRef = React.useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<HealerRegisterData>();

  const password = watch('password');

  const toggleSpec = (spec: string) => {
    setSelectedSpecs(prev =>
      prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]
    );
  };

  const toggleLang = (lang: string) => {
    setSelectedLangs(prev =>
      prev.includes(lang) ? prev.filter(l => l !== lang) : [...prev, lang]
    );
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const valid = await trigger(['fullName', 'professionalName', 'email', 'mobile']);
      if (valid) setStep(2);
    } else if (step === 2) {
      const valid = await trigger(['bio', 'qualifications', 'experience', 'pricePerMinute', 'minimumCharges']);
      if (valid) setStep(3);
    }
  };

  // Backend integration point: POST /api/auth/register/healer
  const onSubmit = async (data: HealerRegisterData) => {
    if (selectedSpecs.length === 0) {
      toast.error('Please select at least one area of expertise.');
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/register-healer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: data.fullName,
          professionalName: data.professionalName,
          email: data.email,
          mobile: data.mobile,
          bio: data.bio,
          qualifications: data.qualifications,
          certifications: data.certifications,
          experienceYears: data.experience,
          specializations: selectedSpecs,
          languages: selectedLangs,
          minimumAmount: data.minimumCharges,
          additionalRate: data.pricePerMinute,
          password: data.password,
        }),
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Registration failed. Please try again.');
        return;
      }
      toast.success('Healer profile submitted! Our team will review and approve within 2 business days.');
      onSwitchToLogin();
    } catch {
      toast.error('Registration failed. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Basic Info' },
    { num: 2, label: 'Professional' },
    { num: 3, label: 'Agreement' },
  ];

  return (
    <div className="fade-in">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-1">Become a Healer</h2>
        <p className="text-sm text-muted-foreground">Share your healing gifts with those who need you</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-1.5 mb-7">
        {steps.map((s, idx) => (
          <React.Fragment key={`healer-step-${s.num}`}>
            <div className="flex items-center gap-1.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step > s.num ? 'bg-success text-white' : step === s.num ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s.num ? <CheckCircle2 size={14} /> : s.num}
              </div>
              <span className={`text-xs font-medium hidden sm:block ${step === s.num ? 'text-primary' : 'text-muted-foreground'}`}>
                {s.label}
              </span>
            </div>
            {idx < steps.length - 1 && <div className={`flex-1 h-px ${step > s.num ? 'bg-success' : 'bg-border'}`} />}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 1 — Basic Info */}
        {step === 1 && (
          <div className="flex flex-col gap-4 slide-up">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-fullName">
                  Full Name <span className="text-danger">*</span>
                </label>
                <input
                  id="h-fullName"
                  type="text"
                  placeholder="Your legal name"
                  className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.fullName ? 'border-danger' : 'border-border'}`}
                  {...register('fullName', { required: 'Full name is required' })}
                />
                {errors.fullName && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.fullName.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-profName">
                  Professional Name <span className="text-danger">*</span>
                </label>
                <input
                  id="h-profName"
                  type="text"
                  placeholder="Name on profile"
                  className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.professionalName ? 'border-danger' : 'border-border'}`}
                  {...register('professionalName', { required: 'Professional name is required' })}
                />
                {errors.professionalName && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.professionalName.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-email">
                Email Address <span className="text-danger">*</span>
              </label>
              <input
                id="h-email"
                type="email"
                placeholder="professional@example.com"
                className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.email ? 'border-danger' : 'border-border'}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
                })}
              />
              {errors.email && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-mobile">
                Mobile Number <span className="text-danger">*</span>
              </label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 bg-muted border border-border rounded-lg text-sm text-muted-foreground font-medium">+91</div>
                <input
                  id="h-mobile"
                  type="tel"
                  placeholder="9876543210"
                  className={`flex-1 px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.mobile ? 'border-danger' : 'border-border'}`}
                  {...register('mobile', {
                    required: 'Mobile is required',
                    pattern: { value: /^[6-9]\d{9}$/, message: 'Enter a valid 10-digit number' },
                  })}
                />
              </div>
              {errors.mobile && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.mobile.message}</p>}
            </div>

            {/* Profile Photo Upload */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Profile Photo</label>
              <p className="text-xs text-muted-foreground mb-2">A professional photo builds client trust</p>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoChange}
              />
              {photoPreview ? (
                <div className="flex items-center gap-4">
                  <img
                    src={photoPreview}
                    alt="Profile photo preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-primary/30"
                  />
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="text-xs font-semibold text-primary border border-primary/30 px-3 py-1.5 rounded-lg hover:bg-primary/5 transition-all"
                    >
                      Change Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPhotoPreview(null); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                      className="text-xs font-semibold text-danger border border-danger/30 px-3 py-1.5 rounded-lg hover:bg-danger/5 transition-all"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => photoInputRef.current?.click()}
                  className="border-2 border-dashed border-border rounded-xl p-6 flex flex-col items-center gap-2 hover:border-primary/40 transition-colors cursor-pointer bg-secondary/50"
                >
                  <Upload size={20} className="text-muted-foreground" />
                  <p className="text-sm font-medium text-muted-foreground">Click to upload or drag here</p>
                  <p className="text-xs text-muted-foreground/70">JPG, PNG — max 5MB</p>
                </div>
              )}
            </div>

            {/* Password */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-pass">
                  Password <span className="text-danger">*</span>
                </label>
                <div className="relative">
                  <input
                    id="h-pass"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    className={`w-full px-4 py-2.5 pr-10 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.password ? 'border-danger' : 'border-border'}`}
                    {...register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Min. 8 characters' },
                    })}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-conf">
                  Confirm <span className="text-danger">*</span>
                </label>
                <div className="relative">
                  <input
                    id="h-conf"
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repeat password"
                    className={`w-full px-4 py-2.5 pr-10 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.confirmPassword ? 'border-danger' : 'border-border'}`}
                    {...register('confirmPassword', {
                      required: 'Please confirm',
                      validate: (v) => v === password || 'Passwords do not match',
                    })}
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary">
                    {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            <button
              type="button"
              onClick={handleNext}
              className="w-full py-3 bg-primary text-primary-foreground font-semibold text-sm rounded-lg hover:bg-primary/90 active:scale-95 transition-all flex items-center justify-center gap-2 mt-1"
            >
              Continue to Professional Info
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Step 2 — Professional */}
        {step === 2 && (
          <div className="flex flex-col gap-4 slide-up">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-bio">
                Professional Biography <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">Describe your healing approach, journey, and what clients can expect</p>
              <textarea
                id="h-bio"
                rows={4}
                placeholder="I am a certified relationship healing specialist with 8+ years of experience..."
                className={`w-full px-4 py-3 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground resize-none ${errors.bio ? 'border-danger' : 'border-border'}`}
                {...register('bio', { required: 'Biography is required', minLength: { value: 100, message: 'Please write at least 100 characters' } })}
              />
              {errors.bio && <p className="mt-1 text-xs text-danger flex items-center gap-1"><AlertCircle size={12} />{errors.bio.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-qual">
                  Qualifications <span className="text-danger">*</span>
                </label>
                <input
                  id="h-qual"
                  type="text"
                  placeholder="e.g. M.A. Psychology, NLP"
                  className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground ${errors.qualifications ? 'border-danger' : 'border-border'}`}
                  {...register('qualifications', { required: 'Qualifications are required' })}
                />
                {errors.qualifications && <p className="mt-1 text-xs text-danger">{errors.qualifications.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-exp">
                  Years of Experience <span className="text-danger">*</span>
                </label>
                <select
                  id="h-exp"
                  className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all ${errors.experience ? 'border-danger' : 'border-border'}`}
                  {...register('experience', { required: 'Experience is required' })}
                >
                  <option value="">Select</option>
                  {['1-2 years', '3-5 years', '6-10 years', '10+ years'].map(e => <option key={`exp-${e}`} value={e}>{e}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5" htmlFor="h-cert">
                Certifications
              </label>
              <input
                id="h-cert"
                type="text"
                placeholder="e.g. Certified Relationship Coach (ICF), EFT Practitioner"
                className="w-full px-4 py-2.5 text-sm bg-card border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground"
                {...register('certifications')}
              />
              <p className="mt-1 text-xs text-muted-foreground">List your certifications, separated by commas</p>
            </div>

            {/* Pricing */}
            <div className="bg-accent-light border border-accent/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Info size={14} className="text-accent" />
                <p className="text-xs font-semibold text-accent">Session Pricing</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="h-minCharge">
                    Minimum Session Charge (₹) <span className="text-danger">*</span>
                  </label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Fixed charge for first 40 minutes</p>
                  <input
                    id="h-minCharge"
                    type="number"
                    placeholder="e.g. 1200"
                    min={500}
                    className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all font-tabular ${errors.minimumCharges ? 'border-danger' : 'border-border'}`}
                    {...register('minimumCharges', {
                      required: 'Minimum charge is required',
                      min: { value: 500, message: 'Minimum ₹500' },
                    })}
                  />
                  {errors.minimumCharges && <p className="mt-1 text-xs text-danger">{errors.minimumCharges.message}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1.5" htmlFor="h-ppm">
                    Additional Rate (₹/min) <span className="text-danger">*</span>
                  </label>
                  <p className="text-[11px] text-muted-foreground mb-1.5">Per minute above 40 minutes</p>
                  <input
                    id="h-ppm"
                    type="number"
                    placeholder="e.g. 20"
                    min={5}
                    className={`w-full px-4 py-2.5 text-sm bg-card border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all font-tabular ${errors.pricePerMinute ? 'border-danger' : 'border-border'}`}
                    {...register('pricePerMinute', {
                      required: 'Rate is required',
                      min: { value: 5, message: 'Minimum ₹5/min' },
                    })}
                  />
                  {errors.pricePerMinute && <p className="mt-1 text-xs text-danger">{errors.pricePerMinute.message}</p>}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Platform service charge (25%) applies. Your earnings are displayed after commission.</p>
            </div>

            {/* Specializations */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Areas of Expertise <span className="text-danger">*</span>
              </label>
              <p className="text-xs text-muted-foreground mb-2">Select all that apply</p>
              <div className="flex flex-wrap gap-2">
                {specializationOptions.map((spec) => (
                  <button
                    key={`spec-${spec}`}
                    type="button"
                    onClick={() => toggleSpec(spec)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                      selectedSpecs.includes(spec)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-muted-foreground border-border hover:border-primary/40 hover:text-primary'
                    }`}
                  >
                    {spec}
                  </button>
                ))}
              </div>
              {selectedSpecs.length === 0 && (
                <p className="mt-1.5 text-xs text-muted-foreground">Select at least one specialization</p>
              )}
            </div>

            {/* Languages */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Languages <span className="text-danger">*</span></label>
              <div className="flex flex-wrap gap-2">
                {languageOptions.map((lang) => (
                  <button
                    key={`hlang-${lang}`}
                    type="button"
                    onClick={() => toggleLang(lang)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-all ${
                      selectedLangs.includes(lang)
                        ? 'bg-accent text-accent-foreground border-accent'
                        : 'bg-card text-muted-foreground border-border hover:border-accent/40 hover:text-accent'
                    }`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 mt-1">
              <button type="button" onClick={() => setStep(1)} className="flex items-center gap-1.5 px-5 py-3 text-sm font-semibold text-muted-foreground border border-border rounded-lg hover:border-primary hover:text-primary transition-all">
                <ChevronLeft size={16} />Back
              </button>
              <button type="button" onClick={handleNext} className="flex-1 py-3 bg-primary text-primary-foreground font-semibold text-sm rounded-lg hover:bg-primary/90 active:scale-95 transition-all flex items-center justify-center gap-2">
                Continue to Agreement <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3 — Agreement */}
        {step === 3 && (
          <div className="flex flex-col gap-4 slide-up">
            <div className="bg-secondary border border-primary/20 rounded-xl p-4 max-h-48 overflow-y-auto scrollbar-thin">
              <p className="text-xs font-bold text-primary mb-2 uppercase tracking-wide">Healer Agreement — Summary</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This agreement governs your participation as a Healer on the Healing52 platform. By accepting, you confirm that:
                (1) All information provided is accurate and complete. (2) You will maintain strict confidentiality of all client information and session content.
                (3) You will conduct sessions with professionalism, empathy, and care. (4) You understand that Healing52 charges a 25% platform commission on session fees.
                (5) You acknowledge that healing services provided are complementary wellness services and not a substitute for emergency medical, psychiatric, legal, or financial professional services.
                (6) You will not make prohibited medical or therapeutic claims. (7) You agree to the platform's cancellation, refund, and payout policies.
                This agreement is version 1.0, effective 11 September 2026. The full agreement text is available on the platform.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {[
                { id: 'agreeHealer', label: 'I have read and agree to the Healer Agreement and Terms of Service' },
                { id: 'agreeAccurate', label: 'I confirm that all information I have provided is accurate and complete' },
                { id: 'agreeConfidential', label: 'I will maintain strict confidentiality of all client information' },
                { id: 'agreeConductProfessional', label: 'I will maintain professional conduct in all sessions' },
                { id: 'agreePaymentStructure', label: 'I understand and accept the platform commission and payment structure' },
              ].map((item) => (
                <div key={`agree-${item.id}`} className="flex items-start gap-3">
                  <input
                    id={item.id}
                    type="checkbox"
                    className="w-4 h-4 mt-0.5 rounded border-border accent-primary"
                    {...register(item.id as keyof HealerRegisterData, { required: 'This confirmation is required' })}
                  />
                  <label htmlFor={item.id} className="text-xs text-muted-foreground leading-relaxed cursor-pointer">
                    {item.label}
                  </label>
                </div>
              ))}
            </div>

            <div className="bg-warning-light border border-warning/30 rounded-xl p-3 flex items-start gap-2">
              <Info size={14} className="text-warning flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Your profile will be reviewed by our admin team after submission. You will receive an email once your profile is approved. Average review time: 2 business days.
              </p>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={() => setStep(2)} className="flex items-center gap-1.5 px-5 py-3 text-sm font-semibold text-muted-foreground border border-border rounded-lg hover:border-primary hover:text-primary transition-all">
                <ChevronLeft size={16} />Back
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-3 bg-primary text-primary-foreground font-semibold text-sm rounded-lg hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {isLoading ? <><Loader2 size={16} className="animate-spin" />Submitting...</> : 'I Agree & Submit Profile'}
              </button>
            </div>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <button type="button" onClick={onSwitchToLogin} className="text-primary font-semibold hover:underline">Sign in</button>
            </p>
          </div>
        )}
      </form>
    </div>
  );
}